import {useEffect, useState} from 'react';
import {useNavigate} from 'react-router-dom';
import {getCoordinatorDetail} from '../../../api/coordiApi';
import {getCoordinatorReviews} from '../../../api/jobApi';
import {findAreaList, getAreaList} from '../../../api/commonApi';
import defaultMaleProfileImg from '../../../assets/images/man_default_img.png';
import defaultFemaleProfileImg from '../../../assets/images/woman_default_img.png';
import {CoordinatorDetailData} from "../../../interface/CoordinatorData";
import {defaultAlert} from "../../../utils/alert";
import {EmptyState} from "../../../components/common/EmptyStateProps";
import {Loading} from "../../../components/common/Loading";

export default function CoordinatorDetailPage() {
    const navigate = useNavigate();
    const [data, setData] = useState<CoordinatorDetailData | null>(null);
    const [reviews, setReviews] = useState<any[]>([]);
    const [activeTab, setActiveTab] = useState<'intro' | 'review'>('intro');
    const [expandedReviews, setExpandedReviews] = useState<number[]>([]);
    const [preferredAreaText, setPreferredAreaText] = useState<string>('');
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetchDetail();
    }, []);

    const fetchDetail = async () => {
        try {
            const res = await getCoordinatorDetail();
            if (res.data?.data) {
                const detail = res.data.data;
                setData(detail);
                await fetchAreaText(detail.preferredAreaMajor, detail.preferredAreaMinor);
                // 코디 코드로 리뷰 조회
                if (detail.coordinatorCode) {
                    fetchReviews(detail.coordinatorCode);
                }
            }
        } catch (error) {
            console.error('Failed to fetch coordinator detail:', error);
            defaultAlert('코디네이터 정보를 불러오는데 실패했습니다.');
            navigate(-1);
        } finally {
            setLoading(false);
        }
    };

    const fetchAreaText = async (majorCode: string, minorCode: string) => {
        if (!majorCode) {
            setPreferredAreaText('미설정');
            return;
        }
        try {
            const mainRes = await getAreaList();
            const mainAreas: any[] = mainRes.data?.data ?? [];
            const mainArea = mainAreas.find((a: any) => a.coCode === majorCode);
            const mainName = mainArea?.displayName || mainArea?.coName || majorCode;

            if (!minorCode) {
                setPreferredAreaText(mainName);
                return;
            }

            const subRes = await findAreaList(majorCode);
            const subAreas: any[] = subRes.data?.data ?? [];
            const subArea = subAreas.find((a: any) => a.coCode === minorCode);
            const subName = subArea?.displayName || subArea?.coName || minorCode;

            setPreferredAreaText(`${mainName} ${subName}`);
        } catch {
            setPreferredAreaText([majorCode, minorCode].filter(Boolean).join(' '));
        }
    };

    const fetchReviews = async (coordinatorCode: string) => {
        try {
            const res = await getCoordinatorReviews(coordinatorCode);
            setReviews(res.data?.data || []);
        } catch (error) {
            console.error('Failed to fetch reviews:', error);
        }
    };

    const getCareerText = (hasCareer: boolean, year: number, month: number) => {
        if (!hasCareer) return '미경력';
        const parts = [];
        if (year > 0) parts.push(`${year}년`);
        if (month > 0) parts.push(`${month}개월`);
        return parts.length > 0 ? `경력 ${parts.join(' ')}` : '미경력';
    };

    const getGenderText = (gender: string) => gender === 'M' ? '남성' : '여성';
    const getGenderIcon = (gender: string) => gender === 'M' ? 'fa-mars' : 'fa-venus';

    const renderStars = (rating: number) => {
        return [...Array(5)].map((_, index) => (
            <span key={index} className={index < rating ? 'on' : ''}>
                <i className="fa-solid fa-star"></i>
            </span>
        ));
    };

    const toggleReview = (index: number) => {
        setExpandedReviews(prev =>
            prev.includes(index) ? prev.filter(i => i !== index) : [...prev, index]
        );
    };

    const handleEdit = () => navigate('/coordi/edit');

    if (!data) {
        return <EmptyState message="코디정보 상세를 찾을 수 없습니다."/>;
    }

    return (
        <>
            <Loading loading={loading} onClose={() => setLoading(false)}/>
            <div>
                <div className="bottom-btn">
                    <button type="button" className="btn save" onClick={handleEdit}>정보 수정</button>
                </div>

                <main className="container type_gray">
                    <section className="bg-white">
                        <div className="inner-box">
                            <div className="coordi-profile-box top">
                                <div className="profile-img">
                                    <div className="thumbnail-wrap">
                                        <div className="thumbnail">
                                            <div className="centered">
                                                <img
                                                    src={data.imagePath && data.imagePath !== 'default' ? data.imagePath : (data.gender === 'M' || data.gender === '남성' ? defaultMaleProfileImg : defaultFemaleProfileImg)}
                                                    className="portrait"
                                                    alt="프로필"
                                                    onError={(e) => {
                                                        (e.target as HTMLImageElement).src = data.gender === 'M' || data.gender === '남성' ? defaultMaleProfileImg : defaultFemaleProfileImg;
                                                    }}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="coordi-info">
                                    <p className="name">
                                        {data.memberName}
                                        <span className="career">
                                            {getCareerText(data.hasCareer, data.careerYear, data.careerMonth)}
                                        </span>
                                    </p>
                                    <p className="intro">{data.introduction}</p>
                                </div>
                            </div>
                        </div>
                    </section>

                    <section className="no-gap">
                        <div className="normal-tabs-wrap">
                            <ul className="normal-tabs">
                                <li
                                    className={`tab-link ${activeTab === 'intro' ? 'current' : ''}`}
                                    onClick={() => setActiveTab('intro')}
                                >
                                    소개
                                </li>
                                <li
                                    className={`tab-link ${activeTab === 'review' ? 'current' : ''}`}
                                    onClick={() => setActiveTab('review')}
                                >
                                    후기 ({reviews.length})
                                </li>
                            </ul>
                        </div>

                        {/* 소개 탭 */}
                        <div id="tab-1" className={`normal-tab-content ${activeTab === 'intro' ? 'current' : ''}`}>
                            <div className="bg-white">
                                <div className="coordi-cate">
                                    <div className="ico-info">
                                        <p className="tit">나이</p>
                                        <p className="cont">
                                            <i className="fa-regular fa-file-lines"></i>{data.age}세
                                        </p>
                                    </div>
                                    <div className="ico-info line">
                                        <p className="tit">성별</p>
                                        <p className="cont">
                                            <i className={`fa-solid ${getGenderIcon(data.gender)}`}></i>
                                            {getGenderText(data.gender)}
                                        </p>
                                    </div>
                                    <div className="ico-info line">
                                        <p className="tit">국적</p>
                                        <p className="cont">{data.nationalityName}</p>
                                    </div>
                                </div>
                            </div>

                            <div className="bg-white">
                                <div className="inner-box">
                                    <h3 className="cont-tit"><span className="underline">코디 정보</span></h3>
                                    <div className="round-box mt10">
                                        <div className="round-body">
                                            <div className="info-row">
                                                <span className="label">코디번호</span>
                                                <span className="cont">{data.coordinatorCode}</span>
                                            </div>
                                            <div className="info-row">
                                                <span className="label">이름</span>
                                                <span className="cont">{data.memberName}</span>
                                            </div>
                                            <div className="info-row">
                                                <span className="label">선호지역</span>
                                                <span className="cont">
                                                    {preferredAreaText || '미설정'}
                                                </span>
                                            </div>
                                            <div className="info-row">
                                                <span className="label">돌봄 건수</span>
                                                <span className="cont">{data.completed}건</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="bg-white">
                                <div className="inner-box">
                                    <h3 className="cont-tit"><span className="underline">자격증 정보</span></h3>
                                    {data.certificates && data.certificates.length > 0 ? (
                                        <div className="round-box mt10">
                                            <div className="round-body">
                                                {data.certificates.map((cert, index) => (
                                                    <div key={index} className="info-row">
                                                        <span className="label">{cert.certificateName}</span>
                                                        <span className="cont date">{cert.certificateIssuer}
                                                            <span className="date">({cert.issueDate})</span>
                                                        </span>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="guide-box gray">
                                            <p className="guide_txt">등록된 자격증이 없습니다.</p>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* 후기 탭 */}
                        <div
                            id="tab-2"
                            className={`normal-tab-content bg-white ${activeTab === 'review' ? 'current' : ''}`}
                            style={{display: activeTab === 'review' ? 'block' : 'none'}}
                        >
                            <div className="inner-box">
                                {reviews.length > 0 ? (
                                    reviews.map((review, index) => (
                                        <div key={index} className="review-box">
                                            <div className="grade-info">
                                                <span className="grade-ico">
                                                    {renderStars(review.rating)}
                                                </span>
                                                <span className="date">{review.regDate}</span>
                                                <span className="id">{review.regUser}</span>
                                            </div>
                                            <div
                                                className="review-txt"
                                                style={{
                                                    overflow: expandedReviews.includes(index) ? 'visible' : 'hidden',
                                                    textOverflow: expandedReviews.includes(index) ? 'clip' : 'ellipsis',
                                                    display: expandedReviews.includes(index) ? 'block' : '-webkit-box',
                                                    WebkitLineClamp: expandedReviews.includes(index) ? 'unset' : 3,
                                                    WebkitBoxOrient: 'vertical'
                                                }}
                                            >
                                                {review.content}
                                            </div>
                                            {review.content?.length > 100 && (
                                                <button
                                                    type="button"
                                                    className="btn-more"
                                                    onClick={() => toggleReview(index)}
                                                >
                                                    {expandedReviews.includes(index) ? '접기' : '더보기'}
                                                </button>
                                            )}
                                        </div>
                                    ))
                                ) : (
                                    <div className="guide-box gray">
                                        <p className="guide_txt">등록된 후기가 없습니다.</p>
                                    </div>
                                )}
                            </div>
                        </div>
                    </section>
                </main>
            </div>
        </>
    );
}